
import React, { useEffect, useState } from 'react';
import Timer from './Timer';
import { ImageItem } from '../types';
import OptimizedImage from './OptimizedImage';
import { imagePreloader } from '../utils/imagePreloader';

interface MemorizeScreenProps {
  targetConcept: string;
  imagesToMemorize: ImageItem[];
  displayDuration: number;
  onTimeUp: () => void;
}

const MemorizeScreen: React.FC<MemorizeScreenProps> = ({ targetConcept, imagesToMemorize, displayDuration, onTimeUp }) => {
  const [loadedCount, setLoadedCount] = useState(0);

  useEffect(() => {
    // Preload all images in parallel
    const preloadImages = async () => {
      const urls = imagesToMemorize.map(img => img.url);
      await imagePreloader.preloadImages(urls);
    };
    
    preloadImages();
  }, [imagesToMemorize]);

  const handleImageLoad = () => {
    setLoadedCount(prev => prev + 1);
  };

  const allImagesLoaded = loadedCount === imagesToMemorize.length;

  return (
    <div className="w-full flex flex-col items-center fade-in">
      <div className="p-8 bg-slate-800/50 rounded-2xl shadow-2xl shadow-cyan-500/10 max-w-6xl">
        <h2 className="text-3xl font-bold mb-4 text-cyan-300 text-center">{targetConcept}</h2>
        
        {!allImagesLoaded && (
          <div className="mb-4 text-center">
            <div className="text-cyan-400 text-sm">
              Loading images... {loadedCount}/{imagesToMemorize.length}
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
              <div 
                className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(loadedCount / imagesToMemorize.length) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {allImagesLoaded && <Timer duration={displayDuration / 1000} onComplete={onTimeUp} />}
        
        <div className="mt-6 grid grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {imagesToMemorize.map((image) => (
            <div
              key={image.id}
              className="relative group cursor-pointer transform transition-all duration-300 hover:scale-105"
            >
              <div className="aspect-square rounded-lg overflow-hidden bg-slate-900/50">
                <OptimizedImage
                  src={image.url}
                  alt=""
                  className="w-full h-full object-cover"
                  onLoad={handleImageLoad}
                />
              </div>
            </div>
          ))}
        </div>
        
        <p className="mt-6 text-slate-300 text-center">
          Memorize these images. You'll need to select them from a larger set!
        </p>
      </div>
    </div>
  );
};

export default MemorizeScreen;
